import React, {useEffect, useCallback, useRef, useState} from 'react';
import {Form, Typography, Button} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';
import {ExecutionSetupType, ExternalUserType, UserDTO, UserType} from 'hemwb-api';
import {useDispatch, useSelector} from 'react-redux';
import {authLoggedInUserSelector} from '../../../../../store/auth/selectors';
import UserSelect from '../../../../core/AntdSelect/UserSelect';
import UserCardAntd from '../../../../core/UserCard/UserCardAntd';
import {ModelEditPermissionsFormData} from '../ModelEdit/ModelEditPermissons/ModelEditPermissionsForm';
import {getModelSteward, isModelWeb, isModelTheorem, modifyModelMetadata} from '../../../../../store/model';
import {canEditTHModelAccess} from '../../../../../permissions/usePermissions';
import {hardCodedOptions} from '../ModelEdit/ModelEditMetadata/constants';
import {messageAdd, MessageTypes} from '../../../../../store/message';
import {fullName} from '../../../../../store/user/utils';
import {useExecutionSetupList} from '../../../../../store/executionSetup';
import {useLastValidValue} from '../../../../../hooks/useLastValidValue';
import WarningTextWithTitle from '../../../../core/WarningText/WarningTextWithTitle';
import {useSideBarModal} from '../../../../layout/common/useSideBarModal';
import SideBarModal from '../../../../layout/common/SideBarModal';
import UserSettingsForm from '../ModelEdit/ModelEditPermissons/UserSettingsForm';

const {Text, Title} = Typography;

type StepAccessRedesignedProps = {
  permissions: ModelEditPermissionsFormData;
  onChange: (permissions: ModelEditPermissionsFormData) => void;
};

const StepAccessRedesigned: React.FC<StepAccessRedesignedProps> = ({permissions, onChange}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const loggedInUser = useSelector(authLoggedInUserSelector);
  const [hedsLeadWarning, setHedsLeadWarning] = useState<string | null>(null);
  const webModel = isModelWeb(permissions.model);
  const userPool = useRef<Map<string, UserDTO>>(new Map());
  const {buttonRef, isOpen, openModal, closeModal, user} = useSideBarModal(permissions.model?.uuid);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const calibrationList = useExecutionSetupList(permissions.model?.uuid, ExecutionSetupType.CALIBRATION);
  const scenarioList = useExecutionSetupList(permissions.model?.uuid, ExecutionSetupType.SCENARIO);
  const storedCalibrationList = useLastValidValue(calibrationList);
  const storedScenarioList = useLastValidValue(scenarioList);

  const handleCancelSubModelIds = useCallback(() => setSelectedIds([]), []);

  const handleUpdateSubModelIds = useCallback((id: number, selected: boolean) => {
    setSelectedIds((prev) => (selected ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id)));
  }, []);

  const disableCheckbox = useCallback(() => {
    const userIds: string[] = [];
    permissions.owners?.forEach((owner) => {
      if (owner.id) userIds.push(owner.id);
    });
    if (permissions.vestLead?.id) userIds.push(permissions.vestLead.id);
    const modelSteward = getModelSteward(permissions.model);
    if (modelSteward?.id) userIds.push(modelSteward.id);
    return user?.id ? userIds.includes(user.id) : false;
  }, [permissions, user]);

  const showUserSettings = false;

  // Initialize form + populate user pool
  useEffect(() => {
    const modelSteward = getModelSteward(permissions.model);
    form.setFieldsValue({
      vestLead: permissions.vestLead?.id ? [permissions.vestLead.id] : [],
      modelSteward: modelSteward?.id ? [modelSteward.id] : [],
      owners: permissions.owners?.map((u) => u.id) || [],
      viewers: permissions.viewers?.map((u) => u.id) || [],
      calibrationModelers: permissions.calibrationModelers?.map((u) => u.id) || [],
      scenarioModelers: permissions.scenarioModelers?.map((u) => u.id) || [],
      displayViewers: permissions.displayViewers?.map((u) => u.id) || [],
    });
    [
      permissions.vestLead,
      modelSteward,
      ...(permissions.owners || []),
      ...(permissions.viewers || []),
      ...(permissions.calibrationModelers || []),
      ...(permissions.scenarioModelers || []),
      ...(permissions.displayViewers || []),
    ]
      .filter(Boolean)
      .forEach((u) => {
        if (u!.id) userPool.current.set(u!.id, u!);
      });
  }, [permissions, form]);

  const cacheUser = useCallback((u: UserDTO) => {
    if (u?.id) userPool.current.set(u.id, u);
  }, []);

  const getLastSelectedId = useCallback((value: any): string | undefined => {
    return Array.isArray(value) ? value[value.length - 1] : value;
  }, []);

  const normalizeSingleSelection = useCallback(
    (value: any): string[] => {
      const last = getLastSelectedId(value);
      return last ? [last] : [];
    },
    [getLastSelectedId],
  );

  const validateVestLead = useCallback(
    (userId: string) => {
      if (userPool.current.get(userId)?.userType === UserType.EXTERNALUSER) {
        dispatch(messageAdd('External user cannot be added as Value and Implementation Lead.', MessageTypes.ERROR));
        return false;
      }
      return true;
    },
    [dispatch],
  );

  const validateModelSteward = useCallback(
    (userId: string) => {
      if (userPool.current.get(userId)?.userType === UserType.EXTERNALUSER) {
        dispatch(messageAdd('External user cannot be added as the HEDS Lead.', MessageTypes.ERROR));
        return false;
      }
      return true;
    },
    [dispatch],
  );

  const classificationOptions = hardCodedOptions['information_classification'];
  const theoremModel = isModelTheorem(permissions.model);
  const sensitiveModel =
    classificationOptions && permissions.model?.metadata?.information_classification === classificationOptions[3];
  const errorText = theoremModel ? 'Theorem model' : 'Sensitive model';

  const validateOwners = useCallback(
    (userIds: string[]) => {
      for (const userId of userIds) {
        const u = userPool.current.get(userId);
        if (u?.userType === UserType.EXTERNALUSER) {
          if (u?.externalUserType === ExternalUserType.HTA) {
            dispatch(messageAdd('HTA User cannot be assigned as an Editor.', MessageTypes.ERROR));
            return false;
          }
          if (theoremModel || sensitiveModel) {
            dispatch(messageAdd(`External user access cannot be added as this is a ${errorText}.`, MessageTypes.ERROR));
            return false;
          }
        }
      }
      return true;
    },
    [dispatch, theoremModel, sensitiveModel, errorText],
  );

  const validateViewers = useCallback(
    (userIds: string[]) => {
      for (const userId of userIds) {
        const u = userPool.current.get(userId);
        if (u?.userType === UserType.EXTERNALUSER) {
          if (u?.externalUserType === ExternalUserType.HTA) {
            dispatch(messageAdd('HTA User cannot be assigned as a Model Viewer.', MessageTypes.ERROR));
            return false;
          }
          if (theoremModel || sensitiveModel) {
            dispatch(messageAdd(`External user access cannot be added as this is a ${errorText}.`, MessageTypes.ERROR));
            return false;
          }
        }
      }
      return true;
    },
    [dispatch, theoremModel, sensitiveModel, errorText],
  );

  const validateExternalUserForSensitiveModel = useCallback(
    (userIds: string[]): boolean => {
      if (!(theoremModel || sensitiveModel)) return true;
      for (const userId of userIds) {
        const u = userPool.current.get(userId);
        if (u?.userType === UserType.EXTERNALUSER) {
          dispatch(messageAdd(`External user access cannot be added as this is a ${errorText}.`, MessageTypes.ERROR));
          return false;
        }
      }
      return true;
    },
    [dispatch, theoremModel, sensitiveModel, errorText],
  );

  const handleFormChange = useCallback(
    (changedValues: any, allValues: any) => {
      if (changedValues.vestLead !== undefined) {
        const id = getLastSelectedId(changedValues.vestLead);
        if (id && !validateVestLead(id)) {
          form.setFieldValue('vestLead', permissions.vestLead?.id ? [permissions.vestLead.id] : []);
          return;
        }
      }
      if ('modelSteward' in changedValues) {
        const id = getLastSelectedId(changedValues.modelSteward);
        if (id && !validateModelSteward(id)) {
          const cur = getModelSteward(permissions.model);
          form.setFieldValue('modelSteward', cur?.id ? [cur.id] : []);
          return;
        }

        // Check if user is removing themselves from HEDS Lead
        // Use the CURRENT form value (allValues) not the original prop value (permissions)
        // Show warning ONLY when deleting (clearing) the field, but hide it if they're an Editor
        if (loggedInUser?.id) {
          // Check if user is in the owners (editors) list
          const isUserInOwners = allValues.owners?.includes(loggedInUser.id);
          if (isUserInOwners) {
            setHedsLeadWarning(null);
          } else {
            setHedsLeadWarning(
              "You're removing yourself from the HEDS Lead role. You'll lose edit access to this entry. Add yourself as an Editor if you still need to make changes.",
            );
          }
        } else {
          setHedsLeadWarning(null);
        }
      }
      if (changedValues.owners !== undefined && !validateOwners(changedValues.owners)) {
        form.setFieldValue('owners', permissions.owners?.map((u) => u.id) || []);
        return;
      }
      if (changedValues.viewers !== undefined && !validateViewers(changedValues.viewers)) {
        form.setFieldValue('viewers', permissions.viewers?.map((u) => u.id) || []);
        return;
      }
      if (
        changedValues.calibrationModelers !== undefined &&
        !validateExternalUserForSensitiveModel(changedValues.calibrationModelers)
      ) {
        form.setFieldValue('calibrationModelers', permissions.calibrationModelers?.map((u) => u.id) || []);
        return;
      }
      if (
        changedValues.scenarioModelers !== undefined &&
        !validateExternalUserForSensitiveModel(changedValues.scenarioModelers)
      ) {
        form.setFieldValue('scenarioModelers', permissions.scenarioModelers?.map((u) => u.id) || []);
        return;
      }
      if (
        changedValues.displayViewers !== undefined &&
        !validateExternalUserForSensitiveModel(changedValues.displayViewers)
      ) {
        form.setFieldValue('displayViewers', permissions.displayViewers?.map((u) => u.id) || []);
        return;
      }

      let processed = {
        ...allValues,
        vestLead: normalizeSingleSelection(allValues.vestLead),
        modelSteward: normalizeSingleSelection(allValues.modelSteward),
      };
      if (JSON.stringify(processed) !== JSON.stringify(allValues)) {
        form.setFieldsValue(processed);
      }

      const pool = userPool.current;
      const vestLeadId = getLastSelectedId(processed.vestLead);
      const stewardId = getLastSelectedId(processed.modelSteward);
      const modelSteward = stewardId ? pool.get(stewardId) : undefined;

      const updated: ModelEditPermissionsFormData = {
        ...permissions,
        vestLead: vestLeadId ? pool.get(vestLeadId) : undefined,
        owners: (processed.owners || []).map((id: string) => pool.get(id)).filter(Boolean) as UserDTO[],
        viewers: (processed.viewers || []).map((id: string) => pool.get(id)).filter(Boolean) as UserDTO[],
        calibrationModelers: (processed.calibrationModelers || [])
          .map((id: string) => pool.get(id))
          .filter(Boolean) as UserDTO[],
        scenarioModelers: (processed.scenarioModelers || [])
          .map((id: string) => pool.get(id))
          .filter(Boolean) as UserDTO[],
        displayViewers: (processed.displayViewers || []).map((id: string) => pool.get(id)).filter(Boolean) as UserDTO[],
        model: modelSteward
          ? modifyModelMetadata(permissions.model, {
              isid_of_model_steward: modelSteward.id || '',
              email_for_model_steward: modelSteward.email || '',
              name_of_model_steward: fullName(modelSteward) || '',
            })
          : permissions.model,
      };
      onChange(updated);
    },
    [
      permissions,
      form,
      getLastSelectedId,
      normalizeSingleSelection,
      validateVestLead,
      validateModelSteward,
      validateOwners,
      validateViewers,
      validateExternalUserForSensitiveModel,
      onChange,
    ],
  );

  const renderCardActions = useCallback(
    (currentUser: UserDTO, onDelete: () => void, includeUserSettings = true, canDelete = true) => (
      <div style={{display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0}}>
        {showUserSettings && includeUserSettings && (
          <Button size="small" onClick={(e) => openModal(e, currentUser)}>
            User Settings
          </Button>
        )}
        {canDelete && <Button type="text" size="small" icon={<DeleteOutlined />} onClick={onDelete} />}
      </div>
    ),
    [openModal, showUserSettings],
  );

  const renderUserCard = useCallback(
    (u: UserDTO, onDelete: () => void, opts?: {includeUserSettings?: boolean; canDelete?: boolean}) => (
      <UserCardAntd
        key={u.id}
        user={u}
        showStatusTags
        rightContent={renderCardActions(u, onDelete, opts?.includeUserSettings ?? true, opts?.canDelete ?? true)}
        style={{marginBottom: '8px'}}
      />
    ),
    [renderCardActions],
  );

  // ─── Section header helper ───────────────────────────────────────────────
  const sectionHeader = (type: 'Provide' | 'Grant', title: React.ReactNode) => (
    <div style={{marginBottom: '16px'}}>
      <Text style={{fontSize: '12px', color: '#595959', display: 'block'}}>{type}</Text>
      <Title level={5} style={{margin: 0, fontSize: '16px', fontWeight: 600, color: '#006A63'}}>
        {title}
      </Title>
    </div>
  );

  return (
    <>
      <Text type="secondary" style={{display: 'block', marginBottom: '24px'}}>
        You can update these permissions in the new model entry anytime.
      </Text>

      <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
        {/* Row 1: VestLead (left) | ModelSteward (right) */}
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginBottom: '32px'}}>
          {/* Value and Implementation Lead */}
          <div>
            {sectionHeader(
              'Provide',
              <>
                Value and Implementation Lead <span style={{color: '#ff4d4f'}}>*</span>
              </>,
            )}
            <Form.Item
              name="vestLead"
              label={
                <span>
                  Find user <span style={{color: '#ff4d4f'}}>*</span>
                </span>
              }
              required={false}
              rules={[{required: true, message: 'Please select Value and Implementation Lead'}]}
              colon={false}
              style={{marginBottom: permissions.vestLead ? '8px' : '0'}}
            >
              <UserSelect
                mode="multiple"
                placeholder="Start typing a name"
                onUserSelect={cacheUser}
                selectedUsers={permissions.vestLead ?? undefined}
                getOptionDisabled={(u) => permissions.vestLead?.id === u.id}
                disabled={theoremModel}
                hideSelectedValue
                maxTagCount={0}
                style={{width: '100%'}}
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>
            {permissions.vestLead &&
              renderUserCard(
                permissions.vestLead,
                () => {
                  form.setFieldValue('vestLead', []);
                  onChange({...permissions, vestLead: undefined});
                },
                {includeUserSettings: true, canDelete: !theoremModel},
              )}
          </div>

          {/* HEDS Lead */}
          <div>
            {sectionHeader(
              'Provide',
              <>
                HEDS Lead <span style={{color: '#ff4d4f'}}>*</span>
              </>,
            )}
            <Form.Item
              name="modelSteward"
              label={
                <span>
                  Find user <span style={{color: '#ff4d4f'}}>*</span>
                </span>
              }
              required={false}
              rules={[{required: true, message: 'Please select HEDS Lead'}]}
              colon={false}
              style={{marginBottom: getModelSteward(permissions.model) ? '8px' : '0'}}
            >
              <UserSelect
                mode="multiple"
                placeholder="Start typing a name"
                onUserSelect={cacheUser}
                selectedUsers={getModelSteward(permissions.model) ?? undefined}
                getOptionDisabled={(u) => getModelSteward(permissions.model)?.id === u.id}
                disabled={theoremModel}
                hideSelectedValue
                maxTagCount={0}
                style={{width: '100%'}}
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>

            {/* Display HEDS Lead removal warning */}
            {hedsLeadWarning && (
              <div style={{marginBottom: '16px'}}>
                <WarningTextWithTitle title="Warning" text={hedsLeadWarning} />
              </div>
            )}

            {(() => {
              const steward = getModelSteward(permissions.model);
              return steward
                ? renderUserCard(
                    steward,
                    () => {
                      form.setFieldValue('modelSteward', []);
                      onChange({
                        ...permissions,
                        model: modifyModelMetadata(permissions.model, {
                          isid_of_model_steward: '',
                          email_for_model_steward: '',
                          name_of_model_steward: '',
                        }),
                      });
                      // Manually trigger the warning check since this bypasses onValuesChange
                      if (loggedInUser?.id) {
                        const currentOwners = form.getFieldValue('owners') || [];
                        const isUserInOwners = currentOwners.includes(loggedInUser.id);
                        if (isUserInOwners) {
                          setHedsLeadWarning(null);
                        } else {
                          setHedsLeadWarning(
                            "You're removing yourself from the HEDS Lead role. You'll lose edit access to this entry. Add yourself as an Editor if you still need to make changes.",
                          );
                        }
                      }
                    },
                    {includeUserSettings: false, canDelete: !theoremModel},
                  )
                : null;
            })()}
          </div>
        </div>

        {/* Row 2: Editors (left) | Viewers (right) */}
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginBottom: '32px'}}>
          {/* Add Editor */}
          <div>
            {sectionHeader(
              'Grant',
              <>
                Editor <br />
                (Editing capabilities)
              </>,
            )}
            <Form.Item
              name="owners"
              label={
                <span>
                  Find User <span style={{color: '#ff4d4f'}}>*</span>
                </span>
              }
              required={false}
              rules={[{required: true, message: 'Please add at least one Editor'}]}
              colon={false}
              style={{marginBottom: permissions.owners?.length ? '8px' : '0'}}
            >
              <UserSelect
                mode="multiple"
                placeholder="Start typing a name"
                onUserSelect={cacheUser}
                selectedUsers={permissions.owners}
                getOptionDisabled={(u) => !!permissions.owners?.some((o) => o.id === u.id)}
                disabled={theoremModel}
                hideSelectedValue
                maxTagCount={0}
                style={{width: '100%'}}
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>
            {permissions.owners?.map((owner, index) =>
              renderUserCard(
                owner,
                () => {
                  const current = form.getFieldValue('owners') || [];
                  const next = current.filter((id: string) => id !== owner.id);
                  form.setFieldValue('owners', next);
                  onChange({
                    ...permissions,
                    owners: (permissions.owners || []).filter((u) => next.includes(u.id)),
                  });
                },
                {canDelete: index > 0 && !theoremModel},
              ),
            )}
          </div>

          {/* Permissions to view and download */}
          <div>
            {sectionHeader(
              'Grant',
              <>
                Permissions to view and <br />
                download this Model
              </>,
            )}
            <Form.Item
              name="viewers"
              label="Model Viewers"
              colon={false}
              style={{marginBottom: permissions.viewers?.length ? '8px' : '0'}}
            >
              <UserSelect
                mode="multiple"
                placeholder="Start typing a name"
                onUserSelect={cacheUser}
                selectedUsers={permissions.viewers}
                getOptionDisabled={(u) =>
                  getModelSteward(permissions.model)?.id === u.id ||
                  !!permissions.owners?.some((o) => o.id === u.id) ||
                  !!permissions.viewers?.some((v) => v.id === u.id)
                }
                disabled={theoremModel && !canEditTHModelAccess(permissions.model)}
                hideSelectedValue
                maxTagCount={0}
                style={{width: '100%'}}
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>
            {permissions.viewers?.map((viewer) =>
              renderUserCard(
                viewer,
                () => {
                  const current = form.getFieldValue('viewers') || [];
                  const next = current.filter((id: string) => id !== viewer.id);
                  form.setFieldValue('viewers', next);
                  onChange({
                    ...permissions,
                    viewers: (permissions.viewers || []).filter((u) => next.includes(u.id)),
                  });
                },
                {canDelete: !(theoremModel && !canEditTHModelAccess(permissions.model))},
              ),
            )}
          </div>
        </div>

        {/* Row 3: WebModel-specific roles (only when webModel) */}
        {webModel && (
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px'}}>
            {/* Calibration Management */}
            <div>
              {sectionHeader(
                'Grant',
                <>
                  Calibration Management <br />
                  Permissions{' '}
                  <span style={{fontSize: '12px', fontWeight: 400, color: '#595959'}}>(for WebModel 2.0)</span>
                </>,
              )}
              <Form.Item
                name="calibrationModelers"
                label="Calibation Modelers"
                colon={false}
                style={{marginBottom: permissions.calibrationModelers?.length ? '8px' : '0'}}
              >
                <UserSelect
                  mode="multiple"
                  placeholder="Start typing a name"
                  onUserSelect={cacheUser}
                  selectedUsers={permissions.calibrationModelers}
                  getOptionDisabled={(u) =>
                    getModelSteward(permissions.model)?.id === u.id ||
                    !!permissions.owners?.some((o) => o.id === u.id) ||
                    !!permissions.viewers?.some((v) => v.id === u.id) ||
                    !!permissions.calibrationModelers?.some((c) => c.id === u.id)
                  }
                  hideSelectedValue
                  maxTagCount={0}
                  style={{width: '100%'}}
                  disabled={theoremModel}
                  getPopupContainer={() =>
                    (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                  }
                />
              </Form.Item>
              {permissions.calibrationModelers?.map((u) =>
                renderUserCard(
                  u,
                  () => {
                    const current = form.getFieldValue('calibrationModelers') || [];
                    const next = current.filter((id: string) => id !== u.id);
                    form.setFieldValue('calibrationModelers', next);
                    onChange({
                      ...permissions,
                      calibrationModelers: (permissions.calibrationModelers || []).filter((x) => next.includes(x.id)),
                    });
                  },
                  {canDelete: !theoremModel},
                ),
              )}
            </div>

            {/* Scenario Management */}
            <div>
              {sectionHeader(
                'Grant',
                <>
                  Scenario Management <br />
                  Permissions{' '}
                  <span style={{fontSize: '12px', fontWeight: 400, color: '#595959'}}>(for WebModel 2.0)</span>
                </>,
              )}
              <Form.Item
                name="scenarioModelers"
                label="Scenario Modelers"
                colon={false}
                style={{marginBottom: permissions.scenarioModelers?.length ? '8px' : '0'}}
              >
                <UserSelect
                  mode="multiple"
                  placeholder="Start typing a name"
                  onUserSelect={cacheUser}
                  selectedUsers={permissions.scenarioModelers}
                  getOptionDisabled={(u) =>
                    getModelSteward(permissions.model)?.id === u.id ||
                    !!permissions.owners?.some((o) => o.id === u.id) ||
                    !!permissions.viewers?.some((v) => v.id === u.id) ||
                    !!permissions.calibrationModelers?.some((c) => c.id === u.id) ||
                    !!permissions.scenarioModelers?.some((s) => s.id === u.id)
                  }
                  hideSelectedValue
                  maxTagCount={0}
                  style={{width: '100%'}}
                  disabled={theoremModel}
                  getPopupContainer={() =>
                    (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                  }
                />
              </Form.Item>
              {permissions.scenarioModelers?.map((u) =>
                renderUserCard(
                  u,
                  () => {
                    const current = form.getFieldValue('scenarioModelers') || [];
                    const next = current.filter((id: string) => id !== u.id);
                    form.setFieldValue('scenarioModelers', next);
                    onChange({
                      ...permissions,
                      scenarioModelers: (permissions.scenarioModelers || []).filter((x) => next.includes(x.id)),
                    });
                  },
                  {canDelete: !theoremModel},
                ),
              )}
            </div>

            {/* Display Viewers */}
            <div>
              {sectionHeader(
                'Grant',
                <>
                  Display Viewer <br />
                  Permissions{' '}
                  <span style={{fontSize: '12px', fontWeight: 400, color: '#595959'}}>(for WebModel 2.0)</span>
                </>,
              )}
              <Form.Item
                name="displayViewers"
                label="Display Viewers"
                colon={false}
                style={{marginBottom: permissions.displayViewers?.length ? '8px' : '0'}}
              >
                <UserSelect
                  mode="multiple"
                  placeholder="Start typing a name"
                  onUserSelect={cacheUser}
                  selectedUsers={permissions.displayViewers}
                  getOptionDisabled={(u) =>
                    getModelSteward(permissions.model)?.id === u.id ||
                    !!permissions.owners?.some((o) => o.id === u.id) ||
                    !!permissions.viewers?.some((v) => v.id === u.id) ||
                    !!permissions.calibrationModelers?.some((c) => c.id === u.id) ||
                    !!permissions.scenarioModelers?.some((s) => s.id === u.id) ||
                    !!permissions.displayViewers?.some((d) => d.id === u.id)
                  }
                  hideSelectedValue
                  maxTagCount={0}
                  style={{width: '100%'}}
                  disabled={theoremModel}
                  getPopupContainer={() =>
                    (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                  }
                />
              </Form.Item>
              {permissions.displayViewers?.map((u) =>
                renderUserCard(
                  u,
                  () => {
                    const current = form.getFieldValue('displayViewers') || [];
                    const next = current.filter((id: string) => id !== u.id);
                    form.setFieldValue('displayViewers', next);
                    onChange({
                      ...permissions,
                      displayViewers: (permissions.displayViewers || []).filter((x) => next.includes(x.id)),
                    });
                  },
                  {canDelete: !theoremModel},
                ),
              )}
            </div>
          </div>
        )}
      </Form>

      <SideBarModal className="whiteTheme" isOpen={isOpen} buttonRef={buttonRef} closeModal={closeModal} height={613}>
        <div style={{width: '60rem'}}>
          {buttonRef && user && (
            <UserSettingsForm
              user={user}
              modelUuid={permissions.model?.uuid}
              disableCheckbox={disableCheckbox}
              isOpen={isOpen}
              closeModal={closeModal}
              buttonRef={buttonRef}
              handleUpdateSubModelIds={handleUpdateSubModelIds}
              handleCancelSubModelIds={handleCancelSubModelIds}
              storedCalibrationList={storedCalibrationList}
              storedScenarioList={storedScenarioList}
              selectedIds={selectedIds}
            />
          )}
        </div>
      </SideBarModal>
    </>
  );
};

export default StepAccessRedesigned;
